import { useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import {
  useBrandName,
  useCustomerGroupCode,
  useFeatures,
} from '@jotta/grpc-connect-client/config'
import { useCheckEmail } from '@jotta/grpc-connect-client/customer/useCheckEmail'
import {
  getMaxReferralBonus as getMaxReferralBonusDescriptor,
  sendReferralEmailInvite as sendReferralEmailInviteDescriptor,
} from '@jotta/grpc-connect-openapi/referralQuery'
import type { Field, FormData } from '@jotta/hooks'
import { useForm } from '@jotta/hooks'
import { useLocale } from '@jotta/i18n'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'
import SvgCheckCircle from '@jotta/ui/SvgCheckCircle'
import SvgEnvelopeSimple from '@jotta/ui/SvgEnvelopeSimple'
import SvgGift from '@jotta/ui/SvgGift'
import SvgLogoIcon from '@jotta/ui/SvgLogo_iconLight'
import SvgPlusCircle from '@jotta/ui/SvgPlusCircle'
import SvgTelegramLogo from '@jotta/ui/SvgTelegramLogo'
import SvgXCircle from '@jotta/ui/SvgXCircle'
import { cn } from '@jotta/utils/css'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { formatPriceConnect } from '@jotta/utils/formatPrice'
import { isNotEmptyString } from '@jotta/utils/typeAssertions'

import { PrivateLayout } from '../components/Layout/PrivateLayout'
import { PrivateLayoutCardWrapper } from '../components/Layout/PrivateLayoutCardWrapper'

export function ReferFriendRoute() {
  return (
    <PrivateLayout>
      <PrivateLayoutCardWrapper>
        <ReferFriendLoader />
      </PrivateLayoutCardWrapper>
    </PrivateLayout>
  )
}

function ReferFriendLoader() {
  const { referralsEnabled, isPending } = useFeatures()

  if (isPending) {
    return (
      <LoadingOverlay id="ReferFriendRoute" open={true} position="absolute" />
    )
  } else if (!referralsEnabled) {
    return <Redirect to="/" />
  } else {
    return <ReferFriendPage />
  }
}

function shouldDisplayEmailField(value: Field<any>, index: number) {
  return index === 0 || value.value !== undefined
}

function nonEmptyEmails(data: FormData<any>): string[] {
  return Object.values(data).filter(isNotEmptyString)
}

function ReferFriendPage() {
  const brandName = useBrandName()
  const customerGroupCode = useCustomerGroupCode()
  const locale = useLocale()
  const [success, setSuccess] = useState(false)
  const { mutateAsync: checkEmail } = useCheckEmail({
    throwOnError: false,
  })
  const { data: maxBonus } = useQuery(
    getMaxReferralBonusDescriptor,
    {},
    {
      select: data => data.maxBonus,
    },
  )
  const maxRefereeBonus = maxBonus && formatPriceConnect(maxBonus, locale)

  const { mutateAsync: sendReferralEmailInvite } = useMutation(
    sendReferralEmailInviteDescriptor,
  )

  async function emailValidation(email: string | undefined) {
    if (!email) {
      return
    }
    const checkEmailResponse = await checkEmail({
      email,
      customerGroupCode,
    })
    switch (checkEmailResponse.result.case) {
      case 'emailInvalid':
        return t`We were unable to recognize "${email}" as an email address. Please look for typos and try again`
      case 'emailUsed': // don't show error if email is already used because of privacy
      case 'emailNotUsed':
      case undefined:
        return undefined
      default:
        return exhaustiveGuard(checkEmailResponse.result)
    }
  }

  const form = useForm({
    resetOnSuccess: true,
    fields: {
      email0: {
        type: 'email',
        focusOnMount: true,
        validate: emailValidation,
      },
      email1: {
        type: 'email',
        validate: emailValidation,
      },
      email2: {
        type: 'email',
        validate: emailValidation,
      },
      email3: {
        type: 'email',
        validate: emailValidation,
      },
      email4: {
        type: 'email',
        validate: emailValidation,
      },
    },

    validate(data) {
      return nonEmptyEmails(data).length > 0
        ? undefined
        : t`Please write at least one email`
    },

    async submit(data) {
      await sendReferralEmailInvite({
        emails: nonEmptyEmails(data),
      })
      setSuccess(true)
    },
  })
  const { fields, isSubmitting } = form
  const { email0, email1, email2, email3, email4 } = fields
  const emailFields = [email0, email1, email2, email3, email4]
  const visibleEmailFieldsCount = emailFields.filter(
    shouldDisplayEmailField,
  ).length
  const submitButton = useRef<HTMLButtonElement>(null)

  return (
    <div className="card card-dialog flex max-w-screen-sm flex-col items-center px-16 py-10">
      <PlainBrandIcon icon={SvgLogoIcon} height={60} />

      <div className="body-xl-ln pt-6 text-center">
        {success ? (
          <Trans>Thank you for spreading the word!</Trans>
        ) : (
          <Trans>Give a friend a 10% discount</Trans>
        )}
      </div>

      <p className="body-md-ln py-6 text-center">
        {success ? (
          <Trans>Your invitation has been sent.</Trans>
        ) : (
          <Trans>
            Invite a friend to {brandName}, and they will get 10% off their
            first year when they sign up using your invitation.
          </Trans>
        )}
      </p>

      {success ? (
        <Button
          className="btn btn-tonal"
          icon={SvgGift}
          onClick={() => setSuccess(false)}
        >
          <Trans>Send more invitations</Trans>
        </Button>
      ) : (
        <Form className="flex w-full flex-col px-8" form={form}>
          {emailFields.map((emailField, index) => {
            const hasError = Boolean(emailField.error)
            const valid = emailField.valid

            return (
              <AnimatePresence key={index}>
                {shouldDisplayEmailField(emailField, index) && (
                  <motion.div
                    initial={{ translateY: '-100%' }}
                    animate={{ translateY: '0%' }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    <FormField field={emailField}>
                      <div className="relative flex items-center">
                        <PlainBrandIcon
                          className={cn('absolute left-2', {
                            'stroke-danger': hasError,
                            'stroke-good': valid,
                          })}
                          icon={valid ? SvgCheckCircle : SvgEnvelopeSimple}
                          height={16}
                        />

                        <input
                          className="input w-full pl-8"
                          onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              if (submitButton && submitButton.current) {
                                submitButton.current.focus()
                              }
                            }
                          }}
                          {...emailField.inputProps}
                          placeholder={t`friend@example.com`}
                        />

                        {index > 0 && (
                          <button
                            className="absolute -right-6"
                            onClick={e => {
                              e.preventDefault()
                              emailField.set(undefined)
                            }}
                          >
                            <PlainBrandIcon icon={SvgXCircle} height={20} />
                          </button>
                        )}
                      </div>
                    </FormField>
                  </motion.div>
                )}
              </AnimatePresence>
            )
          })}

          <div className="min-h-14 self-center pb-6 pt-2">
            {visibleEmailFieldsCount < 5 && (
              <Button
                className="btn btn-link"
                icon={SvgPlusCircle}
                onClick={e => {
                  e.preventDefault()
                  emailFields.find((field, index) => {
                    if (!shouldDisplayEmailField(field, index)) {
                      field.set('')
                      return true
                    }
                  })
                }}
              >
                <Trans>Invite another friend</Trans>
              </Button>
            )}
          </div>

          <Button
            className="btn btn-primary self-center"
            type="submit"
            loading={isSubmitting}
            icon={SvgTelegramLogo}
            ref={submitButton}
          >
            <Trans>Send invitation</Trans>
          </Button>
        </Form>
      )}
    </div>
  )
}
